import { createFileRoute, Outlet } from '@tanstack/react-router';

/**
 * PATHLESS LAYOUT: Primary Routes (Non-ThoughtSpot)
 *
 * This layout groups primary navigation routes that don't use ThoughtSpot's prerendered
 * embed pattern or require special handling outside that architecture.
 *
 * Routes in this directory:
 * - `support-access.tsx`: Pure Alchemer UI for account settings and support access toggle
 * - `search.tsx`: ThoughtSpot SearchEmbed with special config requirements (enableCustomColumnGroups, dataPanelV2)
 * - `source.tsx`: Pure Alchemer UI for data source selection and management
 *
 * Architecture Rationale:
 * - These routes don't fit the prerendered embed pattern used by Overview, Dashboards, Charts, etc.
 * - Support Access and Source are pure Alchemer functionality with no ThoughtSpot dependencies
 * - Primary Search uses SearchEmbed directly because it has unique config needs that differ from the
 *   shared prerendered embed configuration
 *
 * These routes differ from:
 * - `_thoughtspot/*`: Routes using prerendered embed with path navigation (Overview, Dashboards, Charts, AI Highlights, Schedules)
 * - `_deep-links/*`: Shareable/bookmarkable routes for direct access via email/Slack/bookmarks
 *
 * URL Pattern: /dashboard/{route-name}
 * The underscore prefix makes this layout "pathless" - it doesn't add a URL segment.
 * Routes inherit the dashboard layout and auth protection from parent routes.
 *
 * Note on URL Changes:
 * - `/source` was moved from root level to `/dashboard/source` during Phase 7 refactoring
 *   to consolidate all primary routes under the dashboard structure
 *
 * Future Considerations:
 * - This layout can be extended with middleware if primary routes need shared logic
 * - Examples: analytics tracking, feature flags, A/B testing, custom error boundaries
 */
export const Route = createFileRoute('/dashboard/_primary-routes')({
  component: RouteComponent,
});

function RouteComponent() {
  return <Outlet />;
}
